<template>
  <div class="tetris-game">
    <div class="game-header">
      <div class="header-left">
        <h2>Math Tetris</h2>
        <p class="subtitle">Answer math to boost your Tetris score!</p>
      </div>
      <div class="stats-panel">
        <div class="stat-box">
          <div class="stat-label">Score</div>
          <div class="stat-value">{{ score }}</div>
        </div>
        <div class="stat-box">
          <div class="stat-label">Level</div>
          <div class="stat-value">{{ level }}</div>
        </div>
        <div class="stat-box highlight">
          <div class="stat-label">Math Streak</div>
          <div class="stat-value">{{ mathStreak }}x</div>
        </div>
        <div class="stat-box">
          <div class="stat-label">Accuracy</div>
          <div class="stat-value">{{ mathAccuracy }}%</div>
        </div>
      </div>
    </div>

    <!-- Game Over Overlay -->
    <transition name="fade">
      <div v-if="gameOver" class="game-over-overlay">
        <div class="game-over-content">
          <h2>Game Over!</h2>
          <p class="final-score">Score: {{ score }}</p>
          <p class="final-stats">Math Accuracy: {{ mathAccuracy }}%</p>
          <p class="final-stats">Best Streak: {{ bestStreak }}x</p>
          <button class="btn-restart" @click="startNewGame">Play Again</button>
        </div>
      </div>
    </transition>

    <!-- Score Popup -->
    <transition name="score-pop">
      <div v-if="showScorePopup" class="score-popup">
        {{ scorePopupText }}
      </div>
    </transition>

    <div class="game-area">
      <!-- Math Quiz Panel (Left Side) -->
      <div class="math-quiz-panel">
        <h3>Quick Math Challenge</h3>
        <div class="math-problem">
          <div class="problem-display">{{ currentMathProblem.display }}</div>
          <div class="problem-hint">Answer to drop piece faster!</div>
        </div>
        
        <div class="answer-options">
          <button 
            v-for="(option, idx) in currentMathProblem.options" 
            :key="idx"
            class="answer-btn"
            :class="{ 
              'correct': lastAnswerResult === 'correct' && option === currentMathProblem.answer,
              'wrong': lastAnswerResult === 'wrong' && option === lastSelectedAnswer
            }"
            @click="selectAnswer(option)"
          >
            {{ option }}
          </button>
        </div>

        <div class="math-stats">
          <div class="stat-item">
            <span class="stat-label">Correct:</span>
            <span class="stat-value">{{ mathCorrect }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Wrong:</span>
            <span class="stat-value">{{ mathWrong }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Current Streak:</span>
            <span class="stat-value streak-highlight">{{ mathStreak }}x</span>
          </div>
        </div>

        <div class="strategy-hint">
          <strong>Strategy:</strong> Build your math streak before clearing lines for maximum points!
        </div>
      </div>

      <!-- Tetris Game Board (Right Side) -->
      <div class="tetris-board-section">
        <div class="game-board-container">
          <div class="game-board" ref="gameBoard">
            <div v-for="(row, y) in board" :key="y" class="board-row">
              <div 
                v-for="(cell, x) in row" 
                :key="x" 
                class="board-cell"
                :class="{ filled: cell }"
                :style="cell ? { backgroundColor: cell.color } : {}"
              >
              </div>
            </div>
            
            <!-- Active Piece Overlay -->
            <div class="active-piece-overlay">
              <div v-if="currentPiece">
                <div 
                  v-for="(block, idx) in activePieceBlocks" 
                  :key="idx"
                  class="active-block"
                  :style="{
                    left: block.x * 30 + 'px',
                    top: block.y * 30 + 'px',
                    backgroundColor: currentPiece.color
                  }"
                >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Side Panel -->
        <div class="side-panel">
          <div class="next-piece-container">
            <h3>Next Piece</h3>
            <div class="next-piece-preview">
              <div 
                v-for="(block, idx) in nextPieceBlocks" 
                :key="idx"
                class="preview-block"
                :style="{
                  left: block.x * 20 + 'px',
                  top: block.y * 20 + 'px',
                  backgroundColor: nextPiece ? nextPiece.color : 'transparent'
                }"
              >
              </div>
            </div>
          </div>

          <div class="controls-info">
            <h3>Controls</h3>
            <div class="control-item">← → Move</div>
            <div class="control-item">↑ Rotate</div>
            <div class="control-item">↓ Soft Drop</div>
            <div class="control-item">Space Hard Drop</div>
            <div class="control-item">P Pause</div>
          </div>

          <div class="multiplier-info">
            <h4>Next Line Clear:</h4>
            <div class="multiplier-display">
              100 × {{ mathStreak }} = <strong>{{ 100 * mathStreak }}</strong> pts
            </div>
          </div>

          <button class="btn-new-game" @click="startNewGame">New Game</button>
        </div>
      </div>
    </div>

    <!-- SEO Content Section -->
    <div class="seo-content-section">
      <div class="seo-scrollview">
        <div class="seo-content">
          <h2>Math Tetris - Strategic Multitasking Brain Training Game</h2>
          
          <h3>What is Math Tetris Strategic Game?</h3>
          <p>Math Tetris is a revolutionary dual-challenge brain training game that combines classic Tetris block-stacking gameplay with continuous quick-math problem solving to create an unprecedented strategic multitasking experience. Unlike traditional educational games, Math Tetris runs two simultaneous game modes: a standard Tetris board where you arrange falling blocks to clear lines, and a separate quick-math challenge panel where you solve arithmetic problems as fast as possible. The innovative twist is that your math performance directly multiplies your Tetris scoring - every correct math answer builds a streak multiplier that applies when you clear Tetris lines, and answering math problems instantly drops your Tetris piece one block lower, creating strategic depth where you must decide when to focus on math versus Tetris positioning. Perfect for cognitive training, attention management, multitasking skill development, mental arithmetic practice, strategic thinking enhancement, and anyone seeking an intellectually challenging gaming experience that exercises multiple brain functions simultaneously.</p>
          
          <h3>Why Play Math Tetris Multitasking Game?</h3>
          <p>Math Tetris offers unique cognitive benefits by forcing your brain to rapidly switch between spatial reasoning (Tetris) and numerical calculation (math quiz), developing real-world multitasking abilities that traditional single-focus games cannot provide. The strategic layer adds depth - do you build a high math streak before clearing lines for massive point multipliers, or do you answer math problems to drop pieces faster when you need precise positioning? This creates moment-to-moment decision-making that keeps gameplay engaging and mentally stimulating. Research shows that multitasking training improves executive function, working memory capacity, attention control, and cognitive flexibility. The game is perfect for students developing study skills, professionals improving productivity, gamers seeking intellectual challenges, educators teaching strategic thinking, and anyone wanting to sharpen their mind through engaging gameplay rather than boring drills.</p>
          
          <h3>How to Play Math Tetris Strategic Challenge</h3>
          <p>The game runs two simultaneous challenges. On the left, a quick-math panel continuously displays arithmetic problems with 5 multiple-choice answers. Click any answer to submit - correct answers build your streak multiplier (2x, 3x, 4x, etc.) and instantly drop your Tetris piece one block, while wrong answers reset your streak to 1x. On the right, play standard Tetris using arrow keys to move and rotate falling pieces. When you complete a Tetris line, you earn 100 points multiplied by your current math streak - so a 5x streak turns a single line into 500 points! Multiple simultaneous line clears multiply the base score (2 lines = 200 base, 3 lines = 300 base, 4 lines = 400 base) before applying your math multiplier, rewarding skilled Tetris stacking. The strategic depth comes from timing - build a high math streak before clearing lines for maximum points, or use math answers to drop pieces faster when you need precise positioning. Advanced players balance both games simultaneously, maintaining high math accuracy while executing complex Tetris strategies.</p>
          
          <h3>Cognitive Benefits and Skill Development</h3>
          <p>Math Tetris trains multiple cognitive domains simultaneously including divided attention (monitoring two games at once), task switching (rapidly alternating between math and Tetris), working memory (remembering piece positions while calculating), mental arithmetic (solving problems under time pressure), spatial reasoning (Tetris piece manipulation), strategic planning (optimizing multiplier timing), decision-making under pressure (when to focus on which game), and sustained concentration (maintaining performance across both challenges). These skills transfer directly to real-world scenarios like studying while monitoring notifications, driving while navigating, cooking multiple dishes simultaneously, or any situation requiring attention management. The game provides measurable feedback through accuracy percentages and streak tracking, allowing players to monitor cognitive improvement over time.</p>
          
          <h3>Strategic Gameplay and Scoring System</h3>
          <p>Master the dual-challenge strategy to maximize scores. Build math streaks by answering consecutive problems correctly - each correct answer increases your multiplier (1x, 2x, 3x, up to unlimited). When you clear Tetris lines, the base score (100 per line, 200 for 2 lines, 300 for 3 lines, 400 for 4 lines) multiplies by your current streak. For example, clearing 4 lines with a 10x math streak earns 4000 points! Wrong math answers reset your streak to 1x, so accuracy matters. The strategic decision is timing - do you build a massive streak before clearing lines, risking a wrong answer that resets everything? Or do you clear lines frequently with moderate multipliers for consistent scoring? Advanced strategies include using math answers to drop pieces faster for precise positioning, deliberately delaying line clears while building streaks, and managing risk versus reward based on your current board state.</p>
          
          <h3>Perfect for Multiple Audiences and Settings</h3>
          <p>Math Tetris appeals to diverse players including competitive gamers seeking intellectual challenges beyond reflexes, students developing multitasking and study skills, professionals training attention management for productivity, educators teaching strategic thinking and decision-making, cognitive training enthusiasts improving brain function, speedrunners optimizing score-per-minute strategies, and casual players wanting engaging mental exercise. The game works excellently in educational settings for teaching attention control, competitive tournaments for high-score challenges, cognitive research for multitasking studies, brain training programs for executive function development, and personal skill development for anyone wanting to improve mental agility through gameplay rather than boring exercises.</p>
          
          <h3>Start Your Strategic Brain Training Today</h3>
          <p>Experience the unique challenge of Math Tetris where every second requires strategic decisions balancing two simultaneous games. Whether you're a puzzle game enthusiast, a competitive gamer, a student improving study skills, or someone seeking meaningful brain training, Math Tetris delivers an intellectually satisfying experience that traditional games cannot match. Start playing now to develop real multitasking abilities, improve mental arithmetic speed, enhance strategic thinking, and discover how high you can push your score multipliers through perfect math accuracy and Tetris mastery. No registration required - just click and start training your brain through strategic gameplay!</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;
const INITIAL_SPEED = 1000; // ms per drop

// Tetrimino shapes
const SHAPES = {
  I: { shape: [[1,1,1,1]], color: '#00f0f0' },
  O: { shape: [[1,1],[1,1]], color: '#f0f000' },
  T: { shape: [[0,1,0],[1,1,1]], color: '#a000f0' },
  S: { shape: [[0,1,1],[1,1,0]], color: '#00f000' },
  Z: { shape: [[1,1,0],[0,1,1]], color: '#f00000' },
  J: { shape: [[1,0,0],[1,1,1]], color: '#0000f0' },
  L: { shape: [[0,0,1],[1,1,1]], color: '#f0a000' }
};

export default {
  name: 'TetrisGame',
  data() {
    return {
      board: [],
      currentPiece: null,
      nextPiece: null,
      score: 0,
      level: 1,
      lines: 0,
      gameOver: false,
      isPaused: false,
      dropInterval: null,
      
      // Math Quiz features
      currentMathProblem: { display: '', answer: 0, options: [] },
      mathStreak: 1,
      mathCorrect: 0,
      mathWrong: 0,
      bestStreak: 1,
      lastAnswerResult: null,
      lastSelectedAnswer: null,
      showScorePopup: false,
      scorePopupText: ''
    };
  },
  computed: {
    activePieceBlocks() {
      if (!this.currentPiece) return [];
      const blocks = [];
      const shape = this.currentPiece.shape;
      
      for (let y = 0; y < shape.length; y++) {
        for (let x = 0; x < shape[y].length; x++) {
          if (shape[y][x]) {
            blocks.push({
              x: this.currentPiece.x + x,
              y: this.currentPiece.y + y
            });
          }
        }
      }
      return blocks;
    },
    
    nextPieceBlocks() {
      if (!this.nextPiece) return [];
      const blocks = [];
      const shape = this.nextPiece.shape;
      
      for (let y = 0; y < shape.length; y++) {
        for (let x = 0; x < shape[y].length; x++) {
          if (shape[y][x]) {
            blocks.push({
              x: x,
              y: y
            });
          }
        }
      }
      return blocks;
    },
    
    dropSpeed() {
      return Math.max(100, INITIAL_SPEED - (this.level - 1) * 50);
    },
    
    mathAccuracy() {
      const total = this.mathCorrect + this.mathWrong;
      if (total === 0) return 100;
      return Math.round((this.mathCorrect / total) * 100);
    }
  },
  mounted() {
    this.initializeBoard();
    this.startNewGame();
    window.addEventListener('keydown', this.handleKeyPress);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyPress);
    if (this.dropInterval) {
      clearInterval(this.dropInterval);
    }
  },
  methods: {
    initializeBoard() {
      this.board = Array(BOARD_HEIGHT).fill(null).map(() => 
        Array(BOARD_WIDTH).fill(null)
      );
    },
    
    startNewGame() {
      this.initializeBoard();
      this.score = 0;
      this.level = 1;
      this.lines = 0;
      this.gameOver = false;
      this.isPaused = false;
      this.mathStreak = 1;
      this.mathCorrect = 0;
      this.mathWrong = 0;
      this.bestStreak = 1;
      this.lastAnswerResult = null;
      this.showScorePopup = false;
      
      this.generateNewMathProblem();
      this.nextPiece = this.generatePiece();
      this.spawnPiece();
      this.startDropping();
    },
    
    generatePiece() {
      const shapeKeys = Object.keys(SHAPES);
      const randomKey = shapeKeys[Math.floor(Math.random() * shapeKeys.length)];
      const shapeData = SHAPES[randomKey];
      
      return {
        shape: shapeData.shape.map(row => [...row]),
        color: shapeData.color,
        x: Math.floor(BOARD_WIDTH / 2) - Math.floor(shapeData.shape[0].length / 2),
        y: 0
      };
    },
    
    generateNewMathProblem() {
      let num1, num2, operator, answer;
      const operators = ['+', '-', '×', '÷'];
      operator = operators[Math.floor(Math.random() * operators.length)];
      
      // Generate based on operator
      if (operator === '+') {
        num1 = Math.floor(Math.random() * 50) + 1;
        num2 = Math.floor(Math.random() * 50) + 1;
        answer = num1 + num2;
      } else if (operator === '-') {
        num1 = Math.floor(Math.random() * 50) + 20;
        num2 = Math.floor(Math.random() * num1) + 1;
        answer = num1 - num2;
      } else if (operator === '×') {
        num1 = Math.floor(Math.random() * 12) + 2;
        num2 = Math.floor(Math.random() * 12) + 2;
        answer = num1 * num2;
      } else { // ÷
        answer = Math.floor(Math.random() * 12) + 1;
        num2 = Math.floor(Math.random() * 10) + 2;
        num1 = answer * num2;
      }
      
      // Generate 4 wrong answers + 1 correct answer
      const options = [answer];
      while (options.length < 5) {
        let wrongAnswer;
        if (operator === '÷' || operator === '×') {
          wrongAnswer = answer + Math.floor(Math.random() * 20) - 10;
        } else {
          wrongAnswer = answer + Math.floor(Math.random() * 30) - 15;
        }
        if (wrongAnswer > 0 && !options.includes(wrongAnswer)) {
          options.push(wrongAnswer);
        }
      }
      
      // Shuffle options
      for (let i = options.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [options[i], options[j]] = [options[j], options[i]];
      }
      
      this.currentMathProblem = {
        display: `${num1} ${operator} ${num2} = ?`,
        answer: answer,
        options: options
      };
    },
    
    selectAnswer(option) {
      if (this.gameOver || this.isPaused) return;
      
      this.lastSelectedAnswer = option;
      const isCorrect = option === this.currentMathProblem.answer;
      
      if (isCorrect) {
        this.mathCorrect++;
        this.mathStreak++;
        if (this.mathStreak > this.bestStreak) {
          this.bestStreak = this.mathStreak;
        }
        this.lastAnswerResult = 'correct';
        
        // Drop piece one block
        this.dropPieceOneBlock();
      } else {
        this.mathWrong++;
        this.mathStreak = 1;
        this.lastAnswerResult = 'wrong';
      }
      
      // Clear feedback after 500ms
      setTimeout(() => {
        this.lastAnswerResult = null;
        this.lastSelectedAnswer = null;
      }, 500);
      
      // Generate new problem
      this.generateNewMathProblem();
    },
    
    dropPieceOneBlock() {
      if (!this.currentPiece) return;
      
      this.currentPiece.y++;
      if (this.checkCollision(this.currentPiece)) {
        this.currentPiece.y--;
        this.lockPiece();
      }
    },
    
    spawnPiece() {
      this.currentPiece = this.nextPiece;
      this.nextPiece = this.generatePiece();
      
      if (this.checkCollision(this.currentPiece)) {
        this.endGame();
      }
    },
    
    startDropping() {
      if (this.dropInterval) {
        clearInterval(this.dropInterval);
      }
      this.dropInterval = setInterval(() => {
        if (!this.isPaused && !this.gameOver) {
          this.moveDown();
        }
      }, this.dropSpeed);
    },
    
    handleKeyPress(e) {
      if (this.gameOver) return;
      
      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.moveLeft();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.moveRight();
          break;
        case 'ArrowDown':
          e.preventDefault();
          this.moveDown();
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.rotate();
          break;
        case ' ':
          e.preventDefault();
          this.hardDrop();
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          this.togglePause();
          break;
      }
    },
    
    moveLeft() {
      if (!this.currentPiece) return;
      this.currentPiece.x--;
      if (this.checkCollision(this.currentPiece)) {
        this.currentPiece.x++;
      }
    },
    
    moveRight() {
      if (!this.currentPiece) return;
      this.currentPiece.x++;
      if (this.checkCollision(this.currentPiece)) {
        this.currentPiece.x--;
      }
    },
    
    moveDown() {
      if (!this.currentPiece) return;
      this.currentPiece.y++;
      if (this.checkCollision(this.currentPiece)) {
        this.currentPiece.y--;
        this.lockPiece();
      }
    },
    
    hardDrop() {
      if (!this.currentPiece) return;
      while (!this.checkCollision(this.currentPiece)) {
        this.currentPiece.y++;
      }
      this.currentPiece.y--;
      this.lockPiece();
    },
    
    rotate() {
      if (!this.currentPiece) return;
      const original = this.currentPiece.shape;
      const rotated = this.rotateMatrix(original);
      this.currentPiece.shape = rotated;
      
      if (this.checkCollision(this.currentPiece)) {
        this.currentPiece.shape = original;
      }
    },
    
    rotateMatrix(matrix) {
      const N = matrix.length;
      const M = matrix[0].length;
      const rotated = Array(M).fill(null).map(() => Array(N).fill(0));
      
      for (let y = 0; y < N; y++) {
        for (let x = 0; x < M; x++) {
          rotated[x][N - 1 - y] = matrix[y][x];
        }
      }
      return rotated;
    },
    
    checkCollision(piece) {
      const shape = piece.shape;
      for (let y = 0; y < shape.length; y++) {
        for (let x = 0; x < shape[y].length; x++) {
          if (shape[y][x]) {
            const boardX = piece.x + x;
            const boardY = piece.y + y;
            
            if (boardX < 0 || boardX >= BOARD_WIDTH || 
                boardY >= BOARD_HEIGHT ||
                (boardY >= 0 && this.board[boardY][boardX])) {
              return true;
            }
          }
        }
      }
      return false;
    },
    
    lockPiece() {
      if (!this.currentPiece) return;
      const shape = this.currentPiece.shape;
      
      for (let y = 0; y < shape.length; y++) {
        for (let x = 0; x < shape[y].length; x++) {
          if (shape[y][x]) {
            const boardY = this.currentPiece.y + y;
            const boardX = this.currentPiece.x + x;
            
            if (boardY >= 0) {
              this.board[boardY][boardX] = {
                color: this.currentPiece.color
              };
            }
          }
        }
      }
      
      this.clearLines();
      this.spawnPiece();
    },
    
    clearLines() {
      let linesCleared = 0;
      
      for (let y = BOARD_HEIGHT - 1; y >= 0; y--) {
        if (this.board[y].every(cell => cell !== null)) {
          this.board.splice(y, 1);
          this.board.unshift(Array(BOARD_WIDTH).fill(null));
          linesCleared++;
          y++; // Check same row again
        }
      }
      
      if (linesCleared > 0) {
        this.lines += linesCleared;
        
        // Calculate score with math streak multiplier
        const baseScore = linesCleared * 100;
        const finalScore = baseScore * this.mathStreak;
        this.score += finalScore;
        
        // Show score popup
        this.scorePopupText = `${linesCleared} line${linesCleared > 1 ? 's' : ''}: ${baseScore} × ${this.mathStreak}x = ${finalScore} pts!`;
        this.showScorePopup = true;
        setTimeout(() => {
          this.showScorePopup = false;
        }, 2000);
        
        // Reset math streak after using it
        this.mathStreak = 1;
        
        // Update level
        this.level = Math.floor(this.lines / 10) + 1;
        this.startDropping(); // Update speed
      }
    },
    
    togglePause() {
      this.isPaused = !this.isPaused;
    },
    
    endGame() {
      this.gameOver = true;
      if (this.dropInterval) {
        clearInterval(this.dropInterval);
      }
    }
  }
};
</script>
<style scoped>
.tetris-game {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 15px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 1400px;
  margin: 0 auto;
  user-select: none;
}

.game-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
  padding: 0 20px;
}

.header-left h2 {
  margin: 0;
  color: white;
  font-size: 32px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.subtitle {
  margin: 5px 0 0 0;
  color: rgba(255,255,255,0.9);
  font-size: 14px;
}

.stats-panel {
  display: flex;
  gap: 15px;
}

.stat-box {
  background: rgba(255,255,255,0.2);
  padding: 10px 15px;
  border-radius: 10px;
  text-align: center;
  min-width: 80px;
  backdrop-filter: blur(10px);
}

.stat-box.highlight {
  background: rgba(255,215,0,0.3);
  border: 2px solid rgba(255,215,0,0.5);
}

.stat-label {
  font-size: 12px;
  color: rgba(255,255,255,0.8);
  text-transform: uppercase;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: white;
  margin-top: 5px;
}

.game-over-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.game-over-content {
  background: white;
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0,0,0,0.3);
}

.game-over-content h2 {
  margin: 0 0 20px 0;
  color: #667eea;
  font-size: 36px;
}

.final-score, .final-equations {
  font-size: 20px;
  margin: 10px 0;
  color: #2c3e50;
}

.btn-restart {
  margin-top: 20px;
  padding: 15px 30px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-restart:hover {
  background: #764ba2;
  transform: scale(1.05);
}

.equation-banner {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  padding: 15px 30px;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.3);
  z-index: 999;
  display: flex;
  gap: 15px;
  align-items: center;
}

.equation-text {
  font-size: 24px;
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
}

.equation-bonus {
  font-size: 18px;
  color: #ffd700;
  font-weight: bold;
}

.game-area {
  display: flex;
  gap: 30px;
  align-items: flex-start;
  width: 100%;
  max-width: 1200px;
}

/* Math Quiz Panel */
.math-quiz-panel {
  flex: 0 0 350px;
  background: rgba(255,255,255,0.15);
  padding: 20px;
  border-radius: 15px;
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.math-quiz-panel h3 {
  margin: 0;
  color: white;
  font-size: 24px;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.math-problem {
  background: rgba(0,0,0,0.3);
  padding: 20px;
  border-radius: 10px;
  text-align: center;
}

.problem-display {
  font-size: 36px;
  font-weight: bold;
  color: white;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  margin-bottom: 10px;
}

.problem-hint {
  font-size: 14px;
  color: rgba(255,255,255,0.8);
}

.answer-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.answer-btn {
  padding: 20px;
  font-size: 24px;
  font-weight: bold;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}

.answer-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,0,0,0.3);
}

.answer-btn.correct {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  animation: correctPulse 0.5s;
}

.answer-btn.wrong {
  background: linear-gradient(135deg, #eb3349 0%, #f45c43 100%);
  animation: wrongShake 0.5s;
}

@keyframes correctPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

@keyframes wrongShake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

.math-stats {
  background: rgba(0,0,0,0.2);
  padding: 15px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.math-stats .stat-item {
  display: flex;
  justify-content: space-between;
  color: white;
  font-size: 16px;
}

.math-stats .stat-label {
  opacity: 0.8;
}

.math-stats .stat-value {
  font-weight: bold;
}

.streak-highlight {
  color: #ffd700 !important;
  font-size: 20px !important;
}

.strategy-hint {
  background: rgba(255,215,0,0.2);
  padding: 15px;
  border-radius: 10px;
  color: white;
  font-size: 14px;
  line-height: 1.5;
  border: 2px solid rgba(255,215,0,0.3);
}

/* Score Popup */
.score-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  padding: 30px 50px;
  border-radius: 20px;
  font-size: 32px;
  font-weight: bold;
  color: white;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  box-shadow: 0 10px 40px rgba(0,0,0,0.4);
  z-index: 1000;
  animation: popIn 0.3s ease-out;
}

@keyframes popIn {
  0% { transform: translate(-50%, -50%) scale(0); }
  100% { transform: translate(-50%, -50%) scale(1); }
}

.score-pop-enter-active {
  animation: popIn 0.3s ease-out;
}

.score-pop-leave-active {
  animation: popIn 0.3s ease-out reverse;
}

/* Tetris Board Section */
.tetris-board-section {
  display: flex;
  gap: 20px;
  flex: 1;
}

.game-board-container {
  background: rgba(0,0,0,0.3);
  padding: 10px;
  border-radius: 10px;
  box-shadow: inset 0 0 20px rgba(0,0,0,0.5);
}

.game-board {
  position: relative;
  background: rgba(0,0,0,0.5);
  border: 2px solid rgba(255,255,255,0.2);
}

.board-row {
  display: flex;
}

.board-cell {
  width: 30px;
  height: 30px;
  border: 1px solid rgba(255,255,255,0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  transition: all 0.3s;
}

.board-cell.filled {
  border-color: rgba(0,0,0,0.3);
}

.board-cell.has-math {
  animation: pulse 1s infinite;
}

.board-cell.equation-highlight {
  animation: highlight 0.5s ease-in-out 3;
  box-shadow: inset 0 0 10px rgba(255,215,0,0.8);
}

.math-value {
  font-size: 16px;
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
  z-index: 10;
}

.active-piece-overlay {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}

.active-block {
  position: absolute;
  width: 30px;
  height: 30px;
  border: 1px solid rgba(0,0,0,0.3);
  display: flex;
  justify-content: center;
  align-items: center;
}

.active-block.has-math {
  animation: pulse 1s infinite;
}

/* Current Problem Display */
.current-problem-display {
  background: rgba(255,255,255,0.3);
  padding: 15px;
  border-radius: 10px;
  margin-bottom: 10px;
  text-align: center;
  backdrop-filter: blur(10px);
}

.problem-text {
  font-size: 32px;
  font-weight: bold;
  color: white;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  margin-bottom: 5px;
}

.problem-hint {
  font-size: 14px;
  color: rgba(255,255,255,0.9);
}

/* Problem on Piece */
.problem-on-piece {
  font-size: 11px;
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
  white-space: nowrap;
}

/* Answer Row */
.answer-row {
  display: flex;
  margin-top: 10px;
  background: rgba(0,0,0,0.5);
  border-radius: 5px;
  padding: 5px;
  gap: 2px;
}

.answer-cell {
  flex: 1;
  min-width: 28px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
  transition: all 0.3s;
  border: 2px solid transparent;
}

.answer-cell.correct-answer {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  border-color: rgba(255,255,255,0.5);
}

.answer-cell.current-column {
  transform: scale(1.1);
  box-shadow: 0 0 10px rgba(255,255,255,0.5);
}

.answer-cell.glow-correct {
  animation: glowGreen 0.5s infinite;
}

@keyframes glowGreen {
  0%, 100% {
    box-shadow: 0 0 20px rgba(0,255,0,0.8);
    border-color: rgba(0,255,0,0.8);
  }
  50% {
    box-shadow: 0 0 30px rgba(0,255,0,1);
    border-color: rgba(0,255,0,1);
  }
}

/* Next Problem Text */
.next-problem-text {
  font-size: 20px;
  font-weight: bold;
  color: white;
  text-align: center;
  margin-bottom: 10px;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
}

.side-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 200px;
}

.next-piece-container {
  background: rgba(255,255,255,0.2);
  padding: 15px;
  border-radius: 10px;
  backdrop-filter: blur(10px);
}

.next-piece-container h3 {
  margin: 0 0 15px 0;
  color: white;
  font-size: 16px;
  text-align: center;
}

.next-piece-preview {
  position: relative;
  width: 100px;
  height: 100px;
  background: rgba(0,0,0,0.3);
  border-radius: 5px;
  margin: 0 auto;
}

.preview-block {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 1px solid rgba(0,0,0,0.3);
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-block.has-math {
  animation: pulse 1s infinite;
}

.math-value-small {
  font-size: 12px;
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
}

.controls-info {
  background: rgba(255,255,255,0.2);
  padding: 15px;
  border-radius: 10px;
  backdrop-filter: blur(10px);
}

.controls-info h3 {
  margin: 0 0 10px 0;
  color: white;
  font-size: 16px;
  text-align: center;
}

.control-item {
  color: white;
  font-size: 13px;
  margin: 5px 0;
  padding: 5px;
  background: rgba(0,0,0,0.2);
  border-radius: 5px;
}

.multiplier-info {
  background: rgba(255,215,0,0.2);
  padding: 15px;
  border-radius: 10px;
  border: 2px solid rgba(255,215,0,0.4);
}

.multiplier-info h4 {
  margin: 0 0 10px 0;
  color: white;
  font-size: 14px;
  text-align: center;
}

.multiplier-display {
  font-size: 20px;
  color: white;
  text-align: center;
  font-weight: bold;
}

.multiplier-display strong {
  color: #ffd700;
  font-size: 24px;
}

.btn-new-game {
  width: 100%;
  padding: 12px;
  background: rgba(255,255,255,0.3);
  color: white;
  border: 2px solid rgba(255,255,255,0.5);
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: all 0.2s;
  backdrop-filter: blur(10px);
}

.btn-new-game:hover {
  background: rgba(255,255,255,0.4);
  transform: scale(1.05);
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

@keyframes highlight {
  0%, 100% { background-color: inherit; }
  50% { background-color: rgba(255,215,0,0.5); }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: all 0.5s;
}

.slide-down-enter, .slide-down-leave-to {
  transform: translateX(-50%) translateY(-100px);
  opacity: 0;
}

/* SEO Content Section */
.seo-content-section {
  margin-top: 20px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  width: 100%;
}

.seo-scrollview {
  height: 300px;
  overflow-y: auto;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 10px;
}

.seo-content {
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.6;
  font-size: 14px;
}

.seo-content h2 {
  font-size: 18px;
  margin-bottom: 15px;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}

.seo-content h3 {
  font-size: 16px;
  margin: 20px 0 10px 0;
  color: #ffd700;
  font-weight: bold;
}

.seo-content p {
  margin-bottom: 15px;
  text-align: justify;
  opacity: 0.95;
}

.seo-scrollview::-webkit-scrollbar {
  width: 6px;
}

.seo-scrollview::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.seo-scrollview::-webkit-scrollbar-thumb {
  background: rgba(255, 215, 0, 0.3);
  border-radius: 3px;
}

.seo-scrollview::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 215, 0, 0.5);
}
</style>
